import { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
// @ts-expect-error("js-cookie exports differ from what ts-lint is expecting and cannot see the default export")
import Cookies from 'js-cookie'

type CookieReadSettings = Omit<CookieInit, 'expires' | 'value'>
type CookieWriteSettings = Omit<CookieInit, 'expires'> & { expires: Date }

function getCookie(options: CookieReadSettings): Promise<CookieListItem> {
    return Promise.resolve(Cookies.get(options.name)).then(result => (result ? { name: options.name, value: result } : null))
}

function setCookie(options: CookieWriteSettings): Promise<void> {
    return Promise.resolve(Cookies.set(options.name, options.value, { expires: options.expires, path: options.path })).then(() => {})
}

function deleteCookie(name: string): Promise<void> {
    Cookies.remove(name)
    return Promise.resolve()
}

function load<EntityT, JsonT = EntityT>(props: CookieState<EntityT, JsonT>, hostPrefix: string): Promise<EntityT> {
    if (props.key) {
        console.debug('CookieStore', `loading ${hostPrefix}-${props.key} from cookieStore`)
        return getCookie({ name: `${hostPrefix}-${props.key}` }).then(result => {
            let jsonValues: JsonT = !!result?.value ? JSON.parse(result?.value) : null
            if ([null, undefined].includes(jsonValues)) {
                console.debug('CookieStore', `${hostPrefix}-${props.key} is empty in cookieStore, falling back to defaultValue`)
                jsonValues = props.defaultValue as any
            }
            return jsonValues !== undefined ? props.fromJson(jsonValues) : ({} as any)
        })
    }
    return Promise.resolve(props.defaultValue)
}

export type CookieState<EntityT, JsonT = EntityT> = {
    key?: string // When key is undefined, this hook acts as a useState hook.
    hostKey?: string
    defaultValue?: EntityT
    fromJson: (json: JsonT) => EntityT
    toJson: (values: EntityT) => JsonT
    load?: boolean // Set this to false if you want to block the initial load.
}

export type CookieResult<EntityT> = {
    loaded: boolean
    values: EntityT
    setValues: (newValues: EntityT, expires?: DateTime) => void
}

/**
 * This hook will load & persist the values to a cookieStore using the given key. When a default value is provided it
 * will return this when cookieStore is empty. When no key is provided, this hook acts as a useState hook.
 */
export default function useCookie<EntityT, JsonT = EntityT>(props: CookieState<EntityT, JsonT>): CookieResult<EntityT> {
    const hostPrefix = useMemo<string>(() => (props.hostKey ? `${props.hostKey}` : 'global'), [props.hostKey])
    const cookieName = useMemo<string | undefined>(() => (props.key ? `${hostPrefix}-${props.key}` : undefined), [hostPrefix, props.key])
    const [values, setValues] = useState<EntityT>()
    const [loaded, setLoaded] = useState<boolean>(false)

    useEffect(() => {
        if (props.load !== false) {
            load(props, hostPrefix)
                .then(values => setValues(values))
                .then(() => setLoaded(true))
        } else {
            console.debug('CookieStore', `'${cookieName}' is not yet ready to be loaded.`)
        }
    }, [props.hostKey, props.key, props.load])

    return {
        loaded,
        values,
        setValues: (newValues: EntityT, expires?: DateTime) => {
            const oldValuesString = JSON.stringify(props.toJson(values))
            const newValuesString = JSON.stringify(props.toJson(newValues))
            if (loaded && oldValuesString !== newValuesString) {
                console.debug('CookieStore', `values ${cookieName} changed, storing...`)
                setValues(newValues)
                if (newValues === undefined && props.key) {
                    deleteCookie(`${cookieName}`).catch(error => {
                        console.error(error)
                    })
                } else if (props.key) {
                    setCookie({
                        expires: (expires ?? DateTime.now().plus({ minutes: 10 })).toBSON(),
                        name: `${cookieName}`,
                        path: window.location.pathname,
                        sameSite: 'strict',
                        value: JSON.stringify(props.toJson(newValues)),
                    }).catch(error => {
                        console.error(error)
                    })
                }
            } else if (loaded) {
                console.debug('CookieStore', `'${cookieName}' was updated, but values didn't change.`)
            } else {
                console.debug('CookieStore', `'${cookieName}' is not yet loaded.`)
            }
        },
    }
}
